import { ref } from 'vue';
import type { ArrayElementType } from '@/core/types/gengeric';
import type {
  EditQuotaInitialValuesStartMutation,
  EditQuotaInitialValuesUpdateMutationVariables,
  EditQuotaReservesUpdateMutationVariables,
  GetQuotaQuery,
} from '@/graphql/generated';
import { useLockResource } from '@/core/composables/useLockResource';
import {
  useEditQuotaInitialValuesCancelMutation,
  useEditQuotaInitialValuesKeepAliveMutation,
  useEditQuotaInitialValuesStartMutation,
  useEditQuotaInitialValuesUpdateMutation,
  useEditQuotaReservesCancelMutation,
  useEditQuotaReservesKeepAliveMutation,
  useEditQuotaReservesStartMutation,
  useEditQuotaReservesUpdateMutation,
  useGetQuotaQuery,
} from '@/graphql/generated';

export type QuotaType = GetQuotaQuery['quota'];
export type QuotaRoomType = ArrayElementType<NonNullable<GetQuotaQuery['quota']>['roomQuotas']>;
export type QuotaInitialValuesRoomCategoryType = ArrayElementType<
  EditQuotaInitialValuesStartMutation['editQuotaAddRooms']['roomQuotas']
>;

const activeQuota = ref<QuotaType>();

export const useQuotaStore = () => {
  const getActiveQuota = async (id: string) => {
    const { request } = useGetQuotaQuery({
      tariffScheduleCid: id,
    });

    const result = await request();

    if (!result.errors && result.data) {
      activeQuota.value = result.data.quota;
    }
  };

  const setActiveQuota = (data: QuotaType) => {
    activeQuota.value = data;
  };

  //#region Установить начальные значения
  const editInitialValuesStart = (id: string) => {
    const { request } = useEditQuotaInitialValuesStartMutation({
      input: { tariffScheduleCid: id },
    });

    return request();
  };

  const editInitialValuesKeepAlive = (id: string) => {
    const { request } = useEditQuotaInitialValuesKeepAliveMutation({
      input: { tariffScheduleCid: id },
    });

    return request();
  };

  const editInitialValuesCancel = (id: string) => {
    const { request } = useEditQuotaInitialValuesCancelMutation({
      input: { tariffScheduleCid: id },
    });

    return request();
  };

  const editInitialValuesUpdate = (payload: EditQuotaInitialValuesUpdateMutationVariables['input']) => {
    const { request } = useEditQuotaInitialValuesUpdateMutation({
      input: payload,
    });

    return request();
  };

  const {
    editResourceStart: editQuotaInitialValuesStart,
    isEditResourceStartLoading: isEditQuotaInitialValuesStartLoading,
    editResourceCancel: editQuotaInitialValuesCancel,
    editResourceUpdate: editQuotaInitialValuesUpdate,
    isEditResourceUpdateLoading: isEditQuotaInitialValuesUpdateLoading,
  } = useLockResource(
    'QuotaInitialValues',
    editInitialValuesStart,
    editInitialValuesKeepAlive,
    editInitialValuesCancel,
    editInitialValuesUpdate,
  );
  //#endregion Установить начальные значения

  //#region Групповая операция
  const editReservesStart = (id: string) => {
    const { request } = useEditQuotaReservesStartMutation({
      input: { tariffScheduleCid: id },
    });

    return request();
  };

  const editReservesKeepAlive = (id: string) => {
    const { request } = useEditQuotaReservesKeepAliveMutation({
      input: { tariffScheduleCid: id },
    });

    return request();
  };

  const editReservesCancel = (id: string) => {
    const { request } = useEditQuotaReservesCancelMutation({
      input: { tariffScheduleCid: id },
    });

    return request();
  };

  const editReservesUpdate = (payload: EditQuotaReservesUpdateMutationVariables['input']) => {
    const { request } = useEditQuotaReservesUpdateMutation({
      input: payload,
    });

    return request();
  };

  const {
    editResourceStart: editQuotaReservesStart,
    isEditResourceStartLoading: isEditQuotaReservesStartLoading,
    editResourceCancel: editQuotaReservesCancel,
    editResourceUpdate: editQuotaReservesUpdate,
    isEditResourceUpdateLoading: isEditQuotaReservesUpdateLoading,
  } = useLockResource(
    'QuotaReserves',
    editReservesStart,
    editReservesKeepAlive,
    editReservesCancel,
    editReservesUpdate,
  );

  //#endregion Установить начальные значения

  return {
    activeQuota,

    getActiveQuota,
    setActiveQuota,

    editQuotaInitialValuesStart,
    isEditQuotaInitialValuesStartLoading,
    editQuotaInitialValuesCancel,
    editQuotaInitialValuesUpdate,
    isEditQuotaInitialValuesUpdateLoading,

    editQuotaReservesStart,
    isEditQuotaReservesStartLoading,
    editQuotaReservesCancel,
    editQuotaReservesUpdate,
    isEditQuotaReservesUpdateLoading,
  };
};
